import React, { useEffect, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faCircleQuestion, faPenToSquare, faPlus, faSearch, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import CustomToast from '../../../../src/components/Toast';
import ConfirmationModal from '../../../../src/components/ConfirmationPopUp';
import SkeletonGrid from '../../../../src/components/SkeletonLoader/SkeletonGrid';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { 
  useDeleteFlashCardMutation,
  useGetVocabularyMutation,
  useMoveQsetQuestionMutation} from '../../../features/authentication/qSetApiSlice';
import NoAssignment from '../../../../src/assets/images/NoAssignment.png';

const FlashCards = () => {
  const navigate = useNavigate();
  const { setId } = useParams();
  const [getFlashCards, {isLoading}] = useGetVocabularyMutation();
  const [deleteFlashCard] = useDeleteFlashCardMutation();
  const [moveFlashcard] = useMoveQsetQuestionMutation();
  const [flashCards, setFlashCards] = useState(null);
  const [openedFlashCard, setOpenedFlashCard] = useState(0);
  const [openDeleteConfirmModel, setOpenDeleteConfirmModel] = useState(false);
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [allFlashCards, setAllFlashCards] = useState([]);
  const [deletedFlashcardId, setDeletedFlashcardId] = useState(0);
  const [flashcardSetName, setFlashcardSetName] = useState('');

  useEffect(() => {
    getFlashCardsAsync();
  }, []);

  const getFlashCardsAsync = async () => {
    let options = {
      id: setId
    };
    const response = await getFlashCards(options).unwrap();
    const positionedFlashcards = response.vocabularyInfo.map((item, index) => { return { ...item, position: index + 1 }; });
    setFlashcardSetName(response.assignmentName);
    setFlashCards(positionedFlashcards);
    setAllFlashCards(positionedFlashcards);
  };

  const handleDelete = async (id) => {
    let option = {
      setId: setId,
      flashcardId: id
    };
    await deleteFlashCard(option).unwrap();
    const updatedFlashcards = flashCards.filter((flashcard) => flashcard.id !== id);
    setFlashCards(updatedFlashcards);
    if(updatedFlashcards.length == 0)
      setAllFlashCards([]);
    CustomToast('Flashcard deleted successfully', 'success');
    setOpenDeleteConfirmModel(false);
    setDeletedFlashcardId(0);
    
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(flashCards);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    if (result.source.index != result.destination.index) {
      updateDocumentPosition(parseInt(result.draggableId), result.source.index + 1, result.destination.index + 1);
      setFlashCards(items);
    }
  };

  const updateDocumentPosition = async (flashcardId, initialPosition, finalPosition) => {
    if (finalPosition > flashCards.length)
      finalPosition = flashCards.length;

    finalPosition = flashCards[finalPosition - 1].position;
    initialPosition = flashCards[initialPosition - 1].position;

    let updatedFlashCards = flashCards;
    if (initialPosition < finalPosition) {
      updatedFlashCards = updatedFlashCards.map(item => {
        if (item.position > initialPosition && item.position <= finalPosition) {
          return { ...item, position: item.position - 1 };
        }
        return item;
      });
    } else {
      updatedFlashCards = updatedFlashCards.map(item => {
        if (item.position >= finalPosition && item.position < initialPosition) {
          return { ...item, position: item.position + 1 };
        }
        return item;
      });
    }

    updatedFlashCards = updatedFlashCards.map(item => {
      if (item.position === initialPosition && item.id == flashcardId) {
        return { ...item, position: finalPosition };
      }
      return item;
    });
    updatedFlashCards.sort((a, b) => a.position - b.position);
    moveFlashcardAsync(updatedFlashCards);
    setFlashCards(updatedFlashCards);
  };

  const moveFlashcardAsync = async (flashCards) => {
    const flashcardIds = flashCards.map((item) => `T${item.id}`);
    let option = {
      setId: setId,
      questionIds: flashcardIds
    };
    await moveFlashcard(option).unwrap();
    CustomToast('Flashcard moved successfully', 'success');
  };

  const handleSearch = (searchText) =>{
    const searchFlashCards = allFlashCards.filter((item)=>item.name.toLowerCase().includes(searchText.toLowerCase()));
    setFlashCards(searchFlashCards);
  };

  return (
    <div className="white-card">
      {!isLoading ? (
        <div id="reportWrapper">
          <div className="d-flex justify-content-between">
            <div className='d-flex'>
              <h1 className='mb-4'>{flashcardSetName}</h1>
              <OverlayTrigger
                key="label-info"
                placement="right"
                overlay={
                  <Tooltip id="info-tooltip" className="custom-tooltip">
                   Click the <strong>Add Flash Card</strong> button to inuput a Vocabulary Term and definition.
                  </Tooltip>
                }>
                <FontAwesomeIcon icon={faCircleQuestion} className="circle-question mx-1 mt-1" />
              </OverlayTrigger>
            </div>
            <div id="buttonGroup" className="ReportToolbarBtn gap-3 align-items-center">
              <button id="back_button" onClick={() => navigate('/mycontent/flashcardsets')} className="btn btn-width btn-castleWhite">
                Back
              </button>
            </div>
          </div>
          <div className={`mb-0 m-0 my-4 ${allFlashCards?.length == 0 && 'no-content-card'}`}>
            {allFlashCards?.length == 0 &&
              <div className="text-center mb-4">
                <img src={NoAssignment} className='my-4' />
                <p>
                  Set is currently empty, use the <strong>Add button</strong> to add to the set.
                </p>
              </div>}
            <div className={`d-flex gap-2 ${allFlashCards?.length == 0 && 'justify-content-center'}`}>
              <div>
                <button onClick={()=> navigate('addflashcard')}
                  className='p-2 btn-questionset text-start w-100'>
                  <div className='align-items-center d-flex'>
                    <div className='align-items-center blue-icon d-flex fs-5 justify-content-center'><FontAwesomeIcon icon={faPlus} /></div>
                    <p className='m-0'>Add Flash Card</p>
                  </div>
                </button>
              </div>
            </div>
            {allFlashCards?.length > 0 &&
            <>
              <div className="custom-search position-relative mb-3 mt-4">
                <FontAwesomeIcon icon={faSearch} />
                <input type="text" placeholder='Search flashcards' className="form-control" onChange={(e)=>handleSearch(e.target.value)}/>
              </div>
            </>
            }
            <div onMouseEnter={() => setIsMouseOver(false)} onMouseLeave={() => setIsMouseOver(true)}>
              {flashCards?.length > 0 &&
                <>
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId='rows'>
                      {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                          {flashCards.map((flashcard, index) => (
                            <Draggable key={flashcard.id} draggableId={`${flashcard.id}`} index={index}>
                              {(provided, snapshot) => (
                                <div key={'castleRes' + index} ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                                  className={`mt-3 border-grey ${snapshot.isDragging && 'drag-style'} ${(isMouseOver && snapshot.isDragging) && ' cursor-not-allowed'}`}>
                                  <div key={index} className='w-100'>
                                    <div className="justify-content-between align-items-center">
                                      <div className="d-flex align-items-center justify-content-between">
                                        <h6>{flashcard.name}</h6>
                                        <div>
                                          <OverlayTrigger
                                            key="edit"
                                            placement="top"
                                            overlay={<Tooltip className="custom-tooltip">
                                               Edit
                                            </Tooltip>}>
                                            <button
                                              className={'btn grid-icon-btn info-icon-btn mr-2'} disabled={flashcard.courseId > 0} onClick={()=> navigate(`${flashcard.id}/addflashcard`)}>
                                              <FontAwesomeIcon icon={faPenToSquare} />
                                            </button>
                                          </OverlayTrigger>

                                          <OverlayTrigger
                                            key="deleteflashcard"
                                            placement="top"
                                            overlay={<Tooltip className="custom-tooltip">Delete</Tooltip>}>
                                            <button className="btn grid-icon-btn danger-icon-btn mr-2" disabled={flashcard.courseId > 0} onClick={() => { setDeletedFlashcardId(flashcard.id); setOpenDeleteConfirmModel(true); }}>
                                              <FontAwesomeIcon icon={faTrashAlt} />
                                            </button>
                                          </OverlayTrigger>
                                          <button
                                            onClick={() => openedFlashCard == flashcard.id ? setOpenedFlashCard(0) : setOpenedFlashCard(flashcard.id)}
                                            className={`rounded-circle chevron-icon z-index10 ${flashcard.id === openedFlashCard && 'set-open'}`}>
                                            <FontAwesomeIcon icon={faChevronRight} />
                                          </button>
                                        </div>
                                      </div>

                                      <div className={`toggleDive ${!(flashcard.id == openedFlashCard) && 'd-none'}`}>
                                        <div className='w-100 mt-4 mb-4 line'></div>
                                        <div className='mb-4' dangerouslySetInnerHTML={{ __html: flashcard.description}}></div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </>}
            </div>
          </div>
          <ConfirmationModal
            isOpen={openDeleteConfirmModel}
            onClose={() => setOpenDeleteConfirmModel(false)}
            titleName='Delete Flashcard'
            onSave={() => handleDelete(deletedFlashcardId)}
            cancelButton='No'
            confirmButton='Yes'
            contentWidth='35%'
            buttonPosition='center'
            content='Deleting this flashcard is permanent. There is no way to restore it. Are you sure you want to delete it?'
          />
        </div>
      ) : (
        <>
          <SkeletonGrid
            rowCount={3}
            colCount={1}
            className='p-1 mt-4'
          />
          <SkeletonGrid
            rowCount={3}
            colCount={1}
            className='p-5 mt-4'
          />
        </>
      )}

    </div>
  );
};

export default FlashCards;
